// src/components/community/ThreadDetail.js
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import ThreadTags from './ThreadTags';
import useThreadStore from '../../hooks/useThreadStore';

const ThreadDetail = ({ threadId, thread, comments, loading, error, onBack }) => {
  const { isLoggedIn, user } = useAuth();
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [localComments, setLocalComments] = useState([]);
  const isMounted = useRef(true);
  
  // Get the addComment function from the thread store
  const { addComment, updateComment, deleteComment } = useThreadStore();

  // Set up mounted ref
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // Update local comments when props change
  useEffect(() => {
    if (comments && isMounted.current) {
      setLocalComments(comments);
    }
  }, [comments]);

  // Format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format the content with paragraphs
  const formatContent = (content) => {
    if (!content) return '';
    return content.split('\n').map((paragraph, index) => (
      <p key={index}>{paragraph}</p>
    ));
  };

  // Check if thread has associated media
  const hasAssociatedMedia = thread && thread.associatedMediaType && thread.associatedMediaId;

  // Handle adding a new comment
  const handleCommentSubmit = async (content) => {
    if (!isMounted.current) return;
    
    try {
      const newComment = await addComment(threadId, content);
      
      // Update the local comments state with the new comment
      if (isMounted.current && newComment) {
        setLocalComments(prevComments => [
          ...prevComments, 
          { ...newComment, replies: [] }
        ]);
      }
    } catch (err) {
      if (isMounted.current) {
        alert('Failed to submit comment. Please try again.');
      }
    }
  };

  // Handle submitting a reply
  const handleReplySubmit = async (content, parentId) => {
    if (!isMounted.current) return;
    
    try {
      const newReply = await addComment(threadId, content, parentId);
      
      // Update local comments state with the new reply
      if (isMounted.current && newReply) {
        setLocalComments(prevComments => 
          prevComments.map(comment => {
            if (comment.id === parentId) {
              return {
                ...comment,
                replies: [...(comment.replies || []), newReply]
              };
            }
            return comment;
          })
        );
        
        // Clear replying state
        setReplyingTo(null);
      }
    } catch (err) {
      if (isMounted.current) {
        alert('Failed to submit reply. Please try again.');
      }
    }
  };

  // Handle updating a comment
  const handleCommentUpdate = async (commentId, content) => {
    if (!isMounted.current) return;
    
    try {
      const updatedComment = await updateComment(commentId, content);
      
      // Update local state to reflect the edit
      if (isMounted.current && updatedComment) {
        setLocalComments(prevComments => {
          // Check if we're updating a top-level comment
          const topLevelCommentIndex = prevComments.findIndex(c => c.id === commentId);
          
          if (topLevelCommentIndex !== -1) {
            // It's a top-level comment
            const newComments = [...prevComments];
            newComments[topLevelCommentIndex] = {
              ...updatedComment,
              replies: prevComments[topLevelCommentIndex].replies || []
            };
            return newComments;
          } else {
            // It's a reply - find the parent comment
            return prevComments.map(comment => {
              if (comment.replies && comment.replies.some(reply => reply.id === commentId)) {
                return {
                  ...comment,
                  replies: comment.replies.map(reply => 
                    reply.id === commentId ? updatedComment : reply
                  )
                };
              }
              return comment;
            });
          }
        });
        
        // Clear editing state
        setEditingComment(null);
      }
    } catch (err) {
      if (isMounted.current) {
        alert('Failed to update comment. Please try again.');
      }
    }
  };

  // Handle deleting a comment
  const handleCommentDelete = async (commentId) => {
    if (!isMounted.current) return;
    
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }
    
    try {
      const success = await deleteComment(commentId);
      
      // Update local state to reflect the deletion
      if (isMounted.current && success) {
        setLocalComments(prevComments => {
          // Check if we're deleting a top-level comment
          const topLevelCommentIndex = prevComments.findIndex(c => c.id === commentId);
          
          if (topLevelCommentIndex !== -1) {
            // It's a top-level comment - remove it
            return prevComments.filter(comment => comment.id !== commentId);
          } else {
            // It's a reply - find the parent comment and remove the reply
            return prevComments.map(comment => {
              if (comment.replies && comment.replies.some(reply => reply.id === commentId)) {
                return {
                  ...comment,
                  replies: comment.replies.filter(reply => reply.id !== commentId)
                };
              }
              return comment;
            });
          }
        });
      }
    } catch (err) {
      if (isMounted.current) {
        alert('Failed to delete comment. Please try again.');
      }
    }
  };

  // Handle starting a reply
  const handleReply = (commentId) => {
    if (!isMounted.current) return;
    
    setReplyingTo(commentId);
    // Cancel editing if we're replying
    setEditingComment(null);
  };

  // Handle cancelling a reply
  const handleCancelReply = () => {
    if (!isMounted.current) return;
    
    setReplyingTo(null);
  };

  // Handle starting to edit a comment
  const handleEdit = (commentId) => {
    if (!isMounted.current) return;
    
    setEditingComment(commentId);
    // Cancel replying if we're editing
    setReplyingTo(null);
  };

  // Handle cancelling an edit
  const handleCancelEdit = () => {
    if (!isMounted.current) return;
    
    setEditingComment(null);
  };

  // Quick reply form submission handler
  const handleQuickReply = (e) => {
    e.preventDefault();
    const input = e.target.elements.quickReply;
    if (input.value.trim()) {
      handleCommentSubmit(input.value);
      input.value = '';
    }
  };

  if (loading) {
    return (
      <div className="thread-detail-loading">
        <div className="spinner"></div>
        <p>Loading thread...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="thread-detail-error">
        <p>{error}</p>
        <button onClick={onBack}>Go Back</button>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="thread-detail-error">
        <p>Thread not found</p>
        <button onClick={onBack}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="thread-detail">
      <button 
        className="back-button"
        onClick={onBack}
      >
        ← Back to Discussions
      </button>
      
      {/* Thread header */}
      <div className="thread-detail-header">
        <h2 className="thread-detail-title">{thread.title}</h2>
        
        <div className="thread-detail-meta">
          <div className="thread-detail-user">
            <img 
              src={thread.userId.profilePicture || "/default-avatar.png"} 
              alt={thread.userId.username} 
              className="thread-user-avatar"
            />
            <span className="thread-username">{thread.userId.username}</span>
          </div>
          <div className="thread-detail-info">
            <span className="thread-date">{formatDate(thread.createdAt)}</span>
            <span className="thread-category">{thread.category}</span>
            <span className="thread-views">{thread.views} views</span>
          </div>
        </div>
        
        {/* Associated Media (if present) */}
        {hasAssociatedMedia && (
          <div className="thread-detail-media">
            <a href={`/${thread.category}/${thread.associatedMediaId}`} key={thread.associatedMediaId} >
            <div className="thread-detail-media-header">
              <div className="thread-detail-media-badge" style={{ background: `var(--${thread.associatedMediaType}-accent-primary)` }}>
                {thread.associatedMediaType === 'anime' ? 'Anime' : 'Manga'} Discussion
              </div>
            </div>
            <div className="thread-detail-media-content">
              {thread.associatedMediaImage && (
                <img 
                  src={thread.associatedMediaImage} 
                  alt={thread.associatedMediaTitle} 
                  className="thread-detail-media-image"
                />
              )}
              <div className="thread-detail-media-info">
                <h3 className="thread-detail-media-title">{thread.associatedMediaTitle}</h3>
                <p className="thread-detail-media-type">
                  {thread.associatedMediaType === 'anime' ? 'Anime' : 'Manga'}
                </p>
              </div>
            </div>
            </a>
          </div>
        )}
        
        {/* Tags without click functionality */}
        {thread.tags && thread.tags.length > 0 && (
          <ThreadTags tags={thread.tags} />
        )}
      </div>
      
      {/* Thread content */}
      <div className="thread-content">
        {formatContent(thread.content)}
      </div>
      
      {/* Reply quick form - single line style */}
      {isLoggedIn && (
        <div className="thread-reply-form">
          <div className="reply-form-container">
            <img 
              src={user.profilePicture || "/default-avatar.png"} 
              alt={user.username} 
              className="reply-user-avatar" 
            />
            <form className="quick-reply-form" onSubmit={handleQuickReply}>
              <input 
                type="text" 
                name="quickReply"
                className="quick-reply-input" 
                placeholder="Write a comment..." 
              />
              <button type="submit" className="quick-reply-button">Post</button>
            </form>
          </div>
        </div>
      )}
      
      {/* Not logged in prompt */}
      {!isLoggedIn && (
        <div className="comment-login-prompt">
          <p>Please log in to join the discussion.</p>
        </div>
      )}
      
      {/* Comment count indicator */}
      <div className="comments-header">
        <div className="comments-count">
          <span className="comments-count-number">{localComments.length}</span>
          <span className="comments-count-text">
            {localComments.length === 1 ? 'Comment' : 'Comments'}
          </span>
        </div>
      </div>
      
      {/* Comments section */}
      <div className="thread-comments">
        {localComments.length === 0 ? (
          <div className="no-comments">
            <p>Be the first to comment on this discussion.</p>
          </div>
        ) : (
          <div className="comments-list">
            {localComments.map(comment => (
              <CommentItem 
                key={comment.id}
                comment={comment}
                currentUser={user}
                onReply={() => handleReply(comment.id)}
                isReplying={replyingTo === comment.id}
                onCancelReply={handleCancelReply}
                onSubmitReply={(content) => handleReplySubmit(content, comment.id)}
                onEdit={() => handleEdit(comment.id)}
                isEditing={editingComment === comment.id}
                onCancelEdit={handleCancelEdit}
                onSubmitEdit={(content) => handleCommentUpdate(comment.id, content)}
                onDelete={() => handleCommentDelete(comment.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreadDetail;